import React, { useState, useEffect, useRef } from 'react';
import { Plus, Minus, X, Divide, Phone, MapPin, Calendar, Users, User, CreditCard, CheckCircle, BookOpen, Info } from 'lucide-react';
import { initAnalytics, logAnalyticsEvent } from './firebase';

function App() {
  const [isVisible, setIsVisible] = useState(false);
  const analyticsInitialized = useRef(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  useEffect(() => {
    if (analyticsInitialized.current) {
      return undefined;
    }
    analyticsInitialized.current = true;

    initAnalytics();

    logAnalyticsEvent('page_view', {
      page_title: document.title,
      page_location: window.location.href,
      page_path: window.location.pathname
    });

    const handleClick = (event) => {
      const target = event.target;
      if (!target || !target.closest) {
        return;
      }

      const clickable = target.closest('a,button');
      if (!clickable) {
        return;
      }

      const label =
        clickable.getAttribute('data-analytics-label') ||
        clickable.getAttribute('aria-label') ||
        clickable.textContent?.trim().slice(0, 80) ||
        clickable.tagName;

      logAnalyticsEvent('click', {
        element: clickable.tagName,
        element_id: clickable.id || undefined,
        label,
        link_url: clickable.getAttribute('href') || undefined,
        page_path: window.location.pathname
      });
    };

    document.addEventListener('click', handleClick);

    return () => {
      document.removeEventListener('click', handleClick);
    };
  }, []);

  const services = [
    {
      title: "Grade 1-3",
      description: "We also assist with basic numeracy and reading to build strong early learning skills",
      subjects: ["Basic Numeracy", "Reading Support", "Strong Foundations"],
      icon: <BookOpen className="w-8 h-8" />,
      color: "blue"
    },
    {
      title: "Grade 4-7 Extra Classes",
      description: "Build a strong foundation in English, Mathematics, and Natural Sciences",
      subjects: ["English", "Mathematics", "Natural Sciences"],
      icon: <Plus className="w-8 h-8" />,
      color: "pink"
    },
    {
      title: "Grade 8-11 Group Sessions",
      description: "Comprehensive support in core subjects with interactive group learning",
      subjects: ["Group Learning", "All Major Subjects", "Every Saturday"],
      icon: <Users className="w-8 h-8" />,
      color: "blue"
    },
    {
      title: "Grade 12 Support",
      description: "Final exam preparation and intensive revision sessions",
      subjects: ["Matric Prep", "Exam Techniques", "Intensive Support"],
      icon: <CheckCircle className="w-8 h-8" />,
      color: "pink"
    },
    {
      title: "One-on-One Lessons",
      description: "Personalized tutoring in Maths, Physics, and Accounting",
      subjects: ["Mathematics", "Physics", "Accounting"],
      icon: <User className="w-8 h-8" />,
      color: "blue"
    }
  ];

  const termFourGroups = [
    {
      title: "Grade 1–9",
      subjects: ["English", "Mathematics", "Natural Sciences"],
      color: "blue"
    },
    {
      title: "Grade 10–12",
      subjects: ["English", "Mathematics / Maths Lit", "Geography", "Physical Sciences", "CAT", "Accounting"],
      color: "pink"
    }
  ];

  const oneOnOnePricing = [
    { duration: "1 Hour", price: "R750", monthly: "R2 900" },
    { duration: "2 Hours", price: "R1 100", monthly: "R4 300" },
    { duration: "3 Hours", price: "R1 500", monthly: "R5 900" }
  ];

  const bankingDetails = [
    { bank: "ABSA", numbers: ["410 1589 359"] },
    { bank: "FNB", numbers: ["630306 55914"] },
    { bank: "CAPITEC BUSINESS", numbers: ["1051719976", "2515355176"] },
    { bank: "NEDBANK", numbers: ["1263449158"] },
    { bank: "TYMEBANK", numbers: ["51035697491"] },
    { bank: "DISCOVERY", numbers: ["15353550660"] },
    { bank: "AFRICAN BANK", numbers: ["20040596249"] }
  ];

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    element?.scrollIntoView({ behavior: 'smooth' });
  };

  const MathIcon = ({ children, delay = 0, color = "pink" }) => (
    <div 
      className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl ${
        color === 'pink' ? 'bg-[#FF4D8C]' : 'bg-[#4DB5FF]'
      } text-white shadow-lg transform transition-all duration-500 hover:scale-110 hover:rotate-12`}
      style={{ 
        animationDelay: `${delay}ms`,
        animation: isVisible ? 'float 3s ease-in-out infinite' : 'none'
      }}
    >
      {children}
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FAFAF8] text-[#2C2C2C] overflow-x-hidden">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fredoka:wght@400;500;600;700&family=Inter:wght@400;500;600&display=swap');
        
        * {
          font-family: 'Inter', sans-serif;
        }
        
        h1, h2, h3, h4, h5, h6, .heading-font {
          font-family: 'Fredoka', sans-serif;
        }

        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes slideInLeft {
          from {
            opacity: 0;
            transform: translateX(-50px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(50px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        .animate-fadeInUp {
          animation: fadeInUp 0.8s ease-out forwards;
        }

        .animate-slideInLeft {
          animation: slideInLeft 0.8s ease-out forwards;
        }

        .animate-slideInRight {
          animation: slideInRight 0.8s ease-out forwards;
        }

        .math-pattern {
          background-image: 
            linear-gradient(45deg, transparent 48%, rgba(77, 181, 255, 0.03) 49%, rgba(77, 181, 255, 0.03) 51%, transparent 52%),
            linear-gradient(-45deg, transparent 48%, rgba(255, 77, 140, 0.03) 49%, rgba(255, 77, 140, 0.03) 51%, transparent 52%);
          background-size: 40px 40px;
        }

        .gradient-text {
          background: linear-gradient(135deg, #FF4D8C 0%, #4DB5FF 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .card-hover {
          transition: all 0.3s ease;
        }

        .card-hover:hover {
          transform: translateY(-8px);
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
        }
      `}</style>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden math-pattern">
        {/* Floating Math Icons */}
        <div className="absolute top-20 left-10 opacity-50">
          <MathIcon delay={0} color="pink"><Plus className="w-8 h-8" /></MathIcon>
        </div>
        <div className="absolute top-40 right-20 opacity-50">
          <MathIcon delay={200} color="blue"><Minus className="w-8 h-8" /></MathIcon>
        </div>
        <div className="absolute bottom-40 left-20 opacity-50">
          <MathIcon delay={400} color="blue"><X className="w-8 h-8" /></MathIcon>
        </div>
        <div className="absolute bottom-20 right-10 opacity-50">
          <MathIcon delay={600} color="pink"><Divide className="w-8 h-8" /></MathIcon>
        </div>

        <div className="container mx-auto px-6 py-20 text-center relative z-10">
          <div className={`mb-8 ${isVisible ? 'animate-fadeInUp' : 'opacity-0'}`}>
            {/* Logo Grid */}
            <div className="flex justify-center mb-6">
              <div className="grid grid-cols-2 gap-3">
                <div className="w-20 h-20 bg-[#FF4D8C] rounded-2xl flex items-center justify-center shadow-lg transform hover:rotate-12 transition-transform">
                  <Plus className="w-10 h-10 text-white" />
                </div>
                <div className="w-20 h-20 bg-[#4DB5FF] rounded-2xl flex items-center justify-center shadow-lg transform hover:rotate-12 transition-transform">
                  <Minus className="w-10 h-10 text-white" />
                </div>
                <div className="w-20 h-20 bg-[#4DB5FF] rounded-2xl flex items-center justify-center shadow-lg transform hover:rotate-12 transition-transform">
                  <Divide className="w-10 h-10 text-white" />
                </div>
                <div className="w-20 h-20 bg-[#FF4D8C] rounded-2xl flex items-center justify-center shadow-lg transform hover:rotate-12 transition-transform">
                  <X className="w-10 h-10 text-white" />
                </div>
              </div>
            </div>
            
            <h1 className="text-7xl md:text-8xl font-bold heading-font mb-4">
              MATHE-MATISE
            </h1>
            <p className="text-2xl md:text-3xl font-semibold tracking-wider text-[#4DB5FF] mb-8">
              LEARNING MADE EASIER
            </p>
          </div>

          <div className={`max-w-2xl mx-auto mb-12 ${isVisible ? 'animate-fadeInUp' : 'opacity-0'}`} style={{ animationDelay: '200ms' }}>
            <p className="text-xl text-gray-600 leading-relaxed">
              Empowering learners in Pretoria with quality tutoring in Mathematics, English, Natural Sciences, Physics, and Accounting. 
              We make learning engaging, effective, and accessible.
            </p>
          </div>

          <div className={`flex flex-col sm:flex-row gap-4 justify-center ${isVisible ? 'animate-fadeInUp' : 'opacity-0'}`} style={{ animationDelay: '400ms' }}>
            <button 
              onClick={() => scrollToSection('pricing')}
              className="px-8 py-4 bg-[#FF4D8C] text-white rounded-full font-semibold text-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 hover:bg-[#ff3378]"
            >
              View Pricing
            </button>
            <a
              href="https://wa.me/27679562899"
              target="_blank"
              rel="noopener noreferrer"
              className="px-8 py-4 bg-white text-[#4DB5FF] border-2 border-[#4DB5FF] rounded-full font-semibold text-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 hover:bg-[#4DB5FF] hover:text-white flex items-center justify-center gap-2"
            >
              <Phone className="w-5 h-5" />
              WhatsApp Us
            </a>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-5xl font-bold heading-font mb-8 gradient-text">
              Why Choose Mathe-Matise?
            </h2>
            <div className="grid md:grid-cols-3 gap-8 mt-12">
              <div className="p-6 rounded-3xl bg-[#FAFAF8] card-hover">
                <div className="w-16 h-16 bg-[#FF4D8C] rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold heading-font mb-3">Proven Results</h3>
                <p className="text-gray-600">Structured learning programs designed to improve grades and build confidence</p>
              </div>
              <div className="p-6 rounded-3xl bg-[#FAFAF8] card-hover">
                <div className="w-16 h-16 bg-[#4DB5FF] rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold heading-font mb-3">Expert Tutors</h3>
                <p className="text-gray-600">Qualified and passionate educators who make learning fun and effective</p>
              </div>
              <div className="p-6 rounded-3xl bg-[#FAFAF8] card-hover">
                <div className="w-16 h-16 bg-[#FF4D8C] rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <MapPin className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold heading-font mb-3">Convenient Location</h3>
                <p className="text-gray-600">Easily accessible venue in Kwaggasrand, Pretoria with Saturday sessions</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-[#FAFAF8] math-pattern">
        <div className="container mx-auto px-6">
          <h2 className="text-5xl font-bold heading-font text-center mb-4">
            Our <span className="gradient-text">Services</span>
          </h2>
          <p className="text-center text-gray-600 mb-16 text-lg max-w-2xl mx-auto">
            Flexible learning options tailored to every student's needs and grade level
          </p>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto">
            {services.map((service, index) => (
              <div 
                key={index}
                className="bg-white rounded-3xl p-8 shadow-lg card-hover"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className={`w-20 h-20 rounded-2xl flex items-center justify-center mb-6 ${
                  service.color === 'pink' ? 'bg-[#FF4D8C]' : 'bg-[#4DB5FF]'
                } text-white`}>
                  {service.icon}
                </div>
                <h3 className="text-2xl font-bold heading-font mb-3">{service.title}</h3>
                <p className="text-gray-600 mb-4">{service.description}</p>
                <div className="space-y-2">
                  {service.subjects.map((subject, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${service.color === 'pink' ? 'bg-[#FF4D8C]' : 'bg-[#4DB5FF]'}`}></div>
                      <span className="text-sm font-medium text-gray-700">{subject}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <h2 className="text-5xl font-bold heading-font text-center mb-4">
            Transparent <span className="gradient-text">Pricing</span>
          </h2>
          <p className="text-center text-gray-600 mb-16 text-lg max-w-2xl mx-auto">
            Affordable rates for quality education. Invest in your child's future today.
          </p>

          {/* Term 4 Group Package */}
          <div className="mx-auto mb-16 w-full max-w-5xl overflow-hidden rounded-[2rem] border-4 border-[#2C2C2C] bg-[#FFF7F1] shadow-2xl">
            <div className="bg-[#2C2C2C] px-5 py-8 text-center text-white sm:px-8">
              <span className="inline-block -rotate-1 rounded-xl bg-[#FF4D8C] px-5 py-2 text-sm font-bold uppercase tracking-wider shadow-[5px_5px_0_#4DB5FF] sm:text-base">
                Group Package · Term 4
              </span>
              <h3 className="heading-font mt-7 text-4xl font-bold sm:text-6xl">
                New <span className="text-[#FF4D8C]">Students</span>
              </h3>
              <div className="mx-auto mt-5 inline-flex max-w-full items-center gap-2 rounded-full border-2 border-white px-4 py-2 text-sm font-semibold sm:text-base">
                <Calendar className="h-5 w-5 shrink-0" />
                <span>Valid October &amp; November 2026</span>
              </div>
            </div>

            <div className="p-5 sm:p-8 lg:p-10">
              <div className="grid gap-6 md:grid-cols-2">
                {termFourGroups.map((group) => (
                  <div key={group.title} className="overflow-hidden rounded-3xl border-4 border-[#2C2C2C] bg-white shadow-[7px_7px_0_#2C2C2C]">
                    <h4 className={`heading-font px-6 py-4 text-2xl font-bold ${
                      group.color === 'pink' ? 'bg-[#FF4D8C] text-white' : 'bg-[#4DB5FF] text-[#2C2C2C]'
                    }`}>
                      {group.title}
                    </h4>
                    <ul className="space-y-4 p-6">
                      {group.subjects.map((subject) => (
                        <li key={subject} className="flex items-start gap-3 text-base font-semibold text-gray-800 sm:text-lg">
                          <span className={`mt-1.5 h-3.5 w-3.5 shrink-0 rounded-sm border-2 border-[#2C2C2C] ${
                            group.color === 'pink' ? 'bg-[#FF4D8C]' : 'bg-[#4DB5FF]'
                          }`}></span>
                          <span>{subject}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>

              <div className="mt-9 grid gap-5 lg:grid-cols-[1fr_auto]">
                <div className="flex flex-col justify-center rounded-3xl bg-[#2C2C2C] p-6 text-white sm:flex-row sm:items-center sm:justify-between sm:gap-8 sm:p-8">
                  <div>
                    <p className="heading-font text-3xl font-bold">4 Lessons</p>
                    <p className="mt-1 text-lg font-semibold text-[#4DB5FF]">8 hours of tutoring</p>
                  </div>
                  <p className="heading-font mt-5 text-5xl font-bold sm:mt-0 sm:text-6xl">R1 200</p>
                </div>
                <div className="flex min-w-[14rem] flex-col items-center justify-center rounded-3xl border-4 border-[#2C2C2C] bg-[#4DB5FF] p-6 text-center shadow-[6px_6px_0_#2C2C2C]">
                  <p className="font-bold">Once-off registration</p>
                  <p className="heading-font mt-1 text-4xl font-bold">R500</p>
                </div>
              </div>

              <div className="mt-8 text-center">
                <a
                  href="https://wa.me/27679562899?text=Hi%20Mahlatse%2C%20I%20would%20like%20to%20enrol%20a%20new%20student%20for%20the%20Term%204%20group%20package."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#FF4D8C] px-8 py-4 text-lg font-bold text-white shadow-lg transition-all duration-300 hover:scale-[1.02] hover:bg-[#ff3378] sm:w-auto"
                  data-analytics-label="Enquire about Term 4 group package"
                >
                  <Phone className="h-5 w-5" />
                  Enquire about this package
                </a>
              </div>
            </div>
          </div>

          {/* One-on-One Pricing */}
          <div className="max-w-4xl mx-auto mb-12">
            <div className="mb-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <h3 className="text-3xl font-bold heading-font text-center">One-on-One Lessons</h3>
              <div className="relative group">
                <button
                  type="button"
                  aria-label="View one-on-one pricing details"
                  className="flex h-9 w-9 items-center justify-center rounded-full border-2 border-[#4DB5FF] bg-white text-[#4DB5FF] transition-colors duration-200 hover:bg-[#4DB5FF] hover:text-white focus:bg-[#4DB5FF] focus:text-white focus:outline-none"
                >
                  <Info className="w-5 h-5" />
                </button>
                <div className="pointer-events-none absolute left-1/2 top-full z-20 mt-3 w-[calc(100vw-2rem)] max-w-[28rem] -translate-x-1/2 rounded-2xl bg-[#1F2937] p-5 text-left text-sm leading-6 text-white opacity-0 shadow-2xl transition-opacity duration-200 group-hover:opacity-100 group-focus-within:opacity-100 sm:w-[28rem]">
                  <p className="font-semibold mb-3">Our exclusive one-on-one tutoring options are as follows:</p>
                  <p className="mb-3">
                    <span className="font-semibold">1 Hour Session:</span><br />
                    Pay-as-you-go - R750 per session<br />
                    Monthly package - R2 900 for 4 lessons
                  </p>
                  <p className="mb-3">
                    <span className="font-semibold">2 Hour Session:</span><br />
                    Pay-as-you-go - R1 100 per session<br />
                    Monthly package - R4 300 for 4 lessons
                  </p>
                  <p>
                    <span className="font-semibold">3 Hour Session (Most Popular):</span><br />
                    Pay-as-you-go - R1 500 per session<br />
                    Monthly package - R5 900 for 4 lessons
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-r from-[#FF4D8C] to-[#4DB5FF] rounded-3xl p-1">
              <div className="bg-white rounded-3xl p-8">
                <p className="text-center text-gray-600 mb-6">
                  Personalized tutoring in <strong>Maths, Physics & Accounting</strong>
                </p>
                <div className="grid md:grid-cols-3 gap-6">
                  {oneOnOnePricing.map((option, index) => (
                    <div key={index} className="bg-[#FAFAF8] rounded-2xl p-6 text-center card-hover">
                      <div className="text-lg font-semibold mb-2">{option.duration}</div>
                      <div className="text-4xl font-bold heading-font gradient-text mb-1">{option.price}</div>
                      <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">Pay as you go</div>
                      <div className="border-t border-gray-200 pt-3 text-sm font-semibold text-gray-700">
                        {option.monthly} <span className="font-normal text-gray-500">/ 4 lessons</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* Venue & Schedule */}
      <section className="py-20 bg-[#4DB5FF] text-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-5xl font-bold heading-font mb-8">When & Where</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-8">
                <Calendar className="w-16 h-16 mx-auto mb-4" />
                <h3 className="text-2xl font-bold heading-font mb-2">Schedule</h3>
                <p className="text-xl">Every Saturday</p>
                <p className="text-lg opacity-90 mt-2">Group sessions and extra classes</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-8">
                <MapPin className="w-16 h-16 mx-auto mb-4" />
                <h3 className="text-2xl font-bold heading-font mb-2">Location</h3>
                <p className="text-xl">Kwaggasrand</p>
                <p className="text-lg opacity-90 mt-2">Pretoria, South Africa</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Banking Details */}
      <section className="py-20 bg-[#FAFAF8]">
        <div className="container mx-auto px-6">
          <h2 className="text-5xl font-bold heading-font text-center mb-4">
            <span className="gradient-text">Banking Details</span>
          </h2>
          <p className="text-center text-gray-600 mb-12 text-lg">
            Reference: <strong>Child's Name, Surname & Grade</strong>
          </p>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
            {bankingDetails.map((bank, index) => (
              <div key={bank.bank} className="bg-white rounded-2xl p-6 shadow-lg card-hover text-center">
                <div className={`mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl text-white ${
                  index % 2 === 0 ? 'bg-[#FF4D8C]' : 'bg-[#4DB5FF]'
                }`}>
                  <CreditCard className="h-7 w-7" />
                </div>
                <h4 className="font-bold text-lg mb-3 heading-font">{bank.bank}</h4>
                <div className="space-y-1">
                  {bank.numbers.map((number, numberIndex) => (
                    <p key={number} className="font-mono text-xl font-bold text-[#4DB5FF]">
                      {numberIndex > 0 && <span className="mr-2 font-sans text-sm text-gray-500">or</span>}
                      {number}
                    </p>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="mx-auto mt-10 max-w-3xl rounded-3xl border-2 border-dashed border-[#4DB5FF] bg-white p-6 text-center shadow-md">
            <p className="font-semibold text-gray-700">
              Please make an immediate payment, use the correct reference, and send your proof of payment via WhatsApp.
            </p>
            <p className="mt-2 text-gray-600">A speed point is also available for payments.</p>
            <a
              href="https://wa.me/27679562899?text=Hi%20Mahlatse%2C%20please%20find%20my%20proof%20of%20payment."
              target="_blank"
              rel="noopener noreferrer"
              className="mt-5 inline-flex items-center justify-center gap-2 rounded-full bg-[#4DB5FF] px-6 py-3 font-semibold text-white shadow-lg transition-all duration-300 hover:scale-105 hover:bg-[#3da4ff]"
              data-analytics-label="Send proof of payment"
            >
              <Phone className="h-5 w-5" />
              Send proof of payment
            </a>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-gradient-to-r from-[#FF4D8C] to-[#4DB5FF] text-white">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-5xl font-bold heading-font mb-8">Ready to Get Started?</h2>
          <p className="text-xl mb-12 max-w-2xl mx-auto opacity-90">
            Contact Mahlatse today to enroll your child or learn more about our programs
          </p>
          
          <div className="max-w-md mx-auto bg-white/10 backdrop-blur-sm rounded-3xl p-8">
            <Phone className="w-16 h-16 mx-auto mb-4" />
            <h3 className="text-2xl font-bold heading-font mb-2">WhatsApp / Call</h3>
            <a 
              href="tel:0679562899"
              className="text-3xl font-bold heading-font hover:underline block mb-4"
            >
              067 956 2899
            </a>
            <p className="text-lg mb-6">Contact: Mahlatse</p>
            <a
              href="https://wa.me/27679562899"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block px-8 py-4 bg-white text-[#4DB5FF] rounded-full font-semibold text-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
            >
              Send Message on WhatsApp
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#2C2C2C] text-white py-12">
        <div className="container mx-auto px-6 text-center">
          <div className="flex justify-center mb-6">
            <div className="grid grid-cols-2 gap-2">
              <div className="w-12 h-12 bg-[#FF4D8C] rounded-xl flex items-center justify-center">
                <Plus className="w-6 h-6 text-white" />
              </div>
              <div className="w-12 h-12 bg-[#4DB5FF] rounded-xl flex items-center justify-center">
                <Minus className="w-6 h-6 text-white" />
              </div>
              <div className="w-12 h-12 bg-[#4DB5FF] rounded-xl flex items-center justify-center">
                <Divide className="w-6 h-6 text-white" />
              </div>
              <div className="w-12 h-12 bg-[#FF4D8C] rounded-xl flex items-center justify-center">
                <X className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>
          <h3 className="text-2xl font-bold heading-font mb-2">MATHE-MATISE</h3>
          <p className="text-[#4DB5FF] font-semibold mb-6">LEARNING MADE EASIER</p>
          <p className="text-gray-400 text-sm">
            © 2026 Mathe-Matise Tutoring. All rights reserved.
          </p>
          <p className="text-gray-400 text-sm mt-2">
            Kwaggasrand, Pretoria • 067 956 2899
          </p>
        </div>
      </footer>
    </div>
  );
}

export default App;
